// src/composables/useSubscription.ts
import { computed } from 'vue';
import type { Restaurant } from '../types';

export interface SubscriptionLimits {
  categories: number;
  items: number;
  allowImages: boolean;
}

export interface GracePeriodInfo {
  isInGrace: boolean;
  isInUltraGrace: boolean;
  daysRemaining: number;
  ultraDaysRemaining: number;
  hasExcessContent: boolean;
  restrictions: {
    readOnly: boolean;          // add/edit locked (deletions still allowed by UI)
    hideImages: boolean;        // hide/disable image features in UI
    hideExcessContent: boolean; // optionally hide items beyond free limits after grace
  };
}

// Plan limits
const FREE_LIMITS: SubscriptionLimits = { categories: 8, items: 30, allowImages: false };
const PRO_LIMITS:  SubscriptionLimits = { categories: 30, items: 200, allowImages: true };
const ULTRA_LIMITS: SubscriptionLimits = { categories: 999999, items: 999999, allowImages: true };

// tolerant date -> Date | null (supports Firestore Timestamp)
function toDate(v: any): Date | null {
  if (!v) return null;
  if (typeof v?.toDate === 'function') return v.toDate();
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

// Read counts exactly the same way as MenuBuilder.vue does
function readCounts(r: Restaurant | null, aggregates?: any): { cats: number; items: number } {
  if (!r) return { cats: 0, items: 0 };

  // Use aggregates data if provided (same as MenuBuilder: agg.value?.categoriesCount)
  if (aggregates) {
    const aggData = aggregates.value || aggregates; // Handle both ref and plain object
    const cats = Number(aggData.categoriesCount || 0);
    const items = Number(aggData.itemsCount || 0);
    return { cats, items };
  }

  // Fallback to checking the restaurant document (legacy)
  const any = r as any;
  const cats =
    any?.stats?.aggregates?.categoriesCount ??
    any?.categoriesCount ??
    0;
  const items =
    any?.stats?.aggregates?.itemsCount ??
    any?.itemsCount ??
    0;
  return { cats: Number(cats) || 0, items: Number(items) || 0 };
}

export function useSubscription(restaurant: Restaurant | null, aggregates?: any) {
  // Subscription tier checks
  const isPro = computed(() => restaurant?.plan === 'pro');
  const isUltra = computed(() => restaurant?.plan === 'ultra');
  const isProOrHigher = computed(() => isPro.value || isUltra.value);

  const limits = computed<SubscriptionLimits>(() => {
    if (isUltra.value) return ULTRA_LIMITS;
    if (isPro.value) return PRO_LIMITS;
    return FREE_LIMITS;
  });

  // Pro Grace period (backend sets proGraceEndDate on cancel/final failure)
  const graceEnd = computed<Date | null>(() => toDate((restaurant as any)?.proGraceEndDate));
  const isInGrace = computed<boolean>(() => !isPro.value && !isUltra.value && !!graceEnd.value && graceEnd.value.getTime() > Date.now());
  const daysRemaining = computed<number>(() => {
    if (!isInGrace.value || !graceEnd.value) return 0;
    const ms = graceEnd.value.getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  });

  // Trial mode from env: 'both' | 'pro' | 'none'
  const trialMode = (import.meta.env.VITE_TRIAL_MODE || 'both') as 'both' | 'pro' | 'none';

  // Trial eligibility — free plan, trial not yet used, and mode allows it
  const isTrialEligible = computed<boolean>(() =>
    trialMode !== 'none' &&
    (restaurant?.plan === 'free' || !restaurant?.plan) &&
    !restaurant?.trialUsed
  );

  // Trial (Stripe trialing status — set during 14-day Pro trial)
  const isInTrial = computed<boolean>(() => restaurant?.subscriptionStatus === 'trialing');
  const trialEnd = computed<Date | null>(() => toDate((restaurant as any)?.currentPeriodEnd));
  const trialDaysRemaining = computed<number>(() => {
    if (!isInTrial.value || !trialEnd.value) return 0;
    const ms = trialEnd.value.getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  });

  // Ultra Grace period (backend sets ultraGraceEndDate on cancel/downgrade)
  const ultraGraceEnd = computed<Date | null>(() => toDate((restaurant as any)?.ultraGraceEndDate));
  const isInUltraGrace = computed<boolean>(() => !isUltra.value && !!ultraGraceEnd.value && ultraGraceEnd.value.getTime() > Date.now());
  const ultraDaysRemaining = computed<number>(() => {
    if (!isInUltraGrace.value || !ultraGraceEnd.value) return 0;
    const ms = ultraGraceEnd.value.getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  });

  // Count actual categories and items
  const counts = computed(() => readCounts(restaurant, aggregates));
  const categoriesCount = computed(() => counts.value.cats);
  const itemsCount = computed(() => counts.value.items);

  // Plan-specific limits
  const FREE_CATEGORIES = 8;
  const FREE_ITEMS = 30;
  const PRO_CATEGORIES = 30;
  const PRO_ITEMS = 200;
  const UNLIMITED_CATEGORIES = 999999;
  const UNLIMITED_ITEMS = 999999;

  // Effective limits based on subscription (including grace periods)
  const effectiveLimits = computed(() => {
    // Ultra or in Ultra grace -> unlimited
    if (isUltra.value || isInUltraGrace.value) {
      return { categories: UNLIMITED_CATEGORIES, items: UNLIMITED_ITEMS };
    }
    // Pro or in Pro grace -> Pro limits
    if (isPro.value || isInGrace.value) {
      return { categories: PRO_CATEGORIES, items: PRO_ITEMS };
    }
    // Free tier
    return { categories: FREE_CATEGORIES, items: FREE_ITEMS };
  });

  // Excess content checks
  const overFreeCats = computed(() => categoriesCount.value > FREE_CATEGORIES);
  const overFreeItems = computed(() => itemsCount.value > FREE_ITEMS);
  const hasImages = computed(() => false); // TODO: implement image checking later

  // EXCESS CONTENT: anything that prevents being on Free after grace
  const hasExcessContent = computed<boolean>(() =>
    overFreeCats.value || overFreeItems.value || hasImages.value
  );

  // Limit checks for creation
  const atCategoryLimit = computed(() => categoriesCount.value >= effectiveLimits.value.categories);
  const atItemLimit = computed(() => itemsCount.value >= effectiveLimits.value.items);

  // Excess amounts for removal
  const excessCategories = computed(() => Math.max(0, categoriesCount.value - FREE_CATEGORIES));
  const excessItems = computed(() => Math.max(0, itemsCount.value - FREE_ITEMS));

  // Editing permissions
  const canEdit = computed<boolean>(() => {
    if (isUltra.value) return true;
    if (isInUltraGrace.value) return true;
    if (isPro.value) return true;
    if (isInGrace.value) return true;
    return !hasExcessContent.value;
  });

  const locked = computed(() => !canEdit.value);
  const canModifyExisting = computed(() => !locked.value);

  // Creation permissions (check against effective limits)
  const canCreateCategory = computed(() =>
    canModifyExisting.value && !atCategoryLimit.value
  );
  const canCreateItem = computed(() =>
    canModifyExisting.value && !atItemLimit.value
  );

  // Image uploads allowed for Pro/Ultra or in grace periods
  const canUploadImages = computed<boolean>(() =>
    isPro.value || isUltra.value || isInGrace.value || isInUltraGrace.value
  );

  // Banner logic
  const shouldShowGraceBanner = computed<boolean>(() =>
    (isInGrace.value && hasExcessContent.value) || isInUltraGrace.value
  );

  const shouldShowPostGraceBanner = computed<boolean>(() =>
    !isPro.value && !isUltra.value && !isInGrace.value && !isInUltraGrace.value && hasExcessContent.value
  );

  const shouldShowUpgradePrompt = computed<boolean>(() =>
    !isUltra.value && (isInUltraGrace.value || isInGrace.value || hasExcessContent.value)
  );

  // Footer logic - show free footer only when truly on free plan (no grace periods)
  const showFreeFooter = computed(() => !isPro.value && !isUltra.value && !isInGrace.value && !isInUltraGrace.value);

  const gracePeriodInfo = computed<GracePeriodInfo>(() => ({
    isInGrace: isInGrace.value,
    isInUltraGrace: isInUltraGrace.value,
    daysRemaining: daysRemaining.value,
    ultraDaysRemaining: ultraDaysRemaining.value,
    hasExcessContent: hasExcessContent.value,
    restrictions: {
      readOnly: !canEdit.value,
      hideImages: !(isPro.value || isUltra.value || isInGrace.value || isInUltraGrace.value),
      hideExcessContent: !isInGrace.value && !isInUltraGrace.value && hasExcessContent.value,
    },
  }));

  return {
    // Subscription tiers
    isPro,
    isUltra,
    isProOrHigher,
    limits,
    gracePeriodInfo,

    // Trial
    isTrialEligible,
    isInTrial,
    trialDaysRemaining,
    upgradeProLabel: computed(() => isTrialEligible.value ? 'Start 14-day Free Trial' : 'Upgrade to Pro'),
    upgradeUltraLabel: computed(() => (isTrialEligible.value && trialMode !== 'pro') ? 'Start 14-day Free Trial' : 'Upgrade to Ultra'),

    // Grace periods
    isInGrace,
    isInUltraGrace,
    daysRemaining,
    ultraDaysRemaining,

    // Counts
    categoriesCount,
    itemsCount,

    // Limits
    effectiveLimits,
    atCategoryLimit,
    atItemLimit,

    // Excess for removal
    excessCategories,
    excessItems,
    hasExcessContent,

    // Permissions
    canEdit,
    locked,
    canModifyExisting,
    canCreateCategory,
    canCreateItem,
    canUploadImages,

    // Banners
    shouldShowGraceBanner,
    shouldShowPostGraceBanner,
    shouldShowUpgradePrompt,
    showFreeFooter,
  };
}
